import { Component, Input } from '@angular/core';
import { finalize } from 'rxjs';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';

// Composants
import { BaseComponent } from 'src/app/@shared/components/abstract/base.component';
import { ConversationDetailsComponent } from "../conversation-details/conversation-details.component";
import { ConversationFormComponent } from '../conversation-form/conversation-form.component';
import { ConversationsListComponent } from "../conversations-list/conversations-list.component";

// Modèles
import { Conversation } from 'src/app/@shared/models/messaging/conversation.model';

@Component({
	selector: 'app-conversations-panel',
	imports: [
		ButtonModule,
		TooltipModule,
		ConversationsListComponent,
		ConversationDetailsComponent,
		ConversationFormComponent
	],
	templateUrl: './conversations-panel.component.html',
	styleUrl: './conversations-panel.component.scss'
})
export class ConversationsPanelComponent extends BaseComponent {

	/*----------------------------------------------------------------------------------------
	 * Gestion de la vue
	 ----------------------------------------------------------------------------------------*/

	// Contexte
	@Input()
	public objectId!: number;
	@Input()
	public entityType!: string;

	/**
	 * Fil à afficher à l'ouverture, désigné par l'adresse (#995). À défaut — absent, inconnu,
	 * ou rattaché à un autre objet — le volet retombe sur sa sélection par défaut, le fil
	 * public « Commentaires ».
	 */
	@Input()
	public initialConversationId: number | null = null;

	public showConversationForm: boolean = false;

	/*----------------------------------------------------------------------------------------
	 * Données
	 ----------------------------------------------------------------------------------------*/

	private _conversations: Conversation[] = [];
	public get conversations(): Conversation[] {
		return this._conversations;
	}
	@Input()
	public set conversations(value: Conversation[]) {
		let emptyArray: Conversation[] = [];
		this._conversations = emptyArray.concat(value);
		// La garantie de l'entrée publique et la sélection par défaut sont faites dans loadData(),
		// une fois les @Input entityType/objectId disponibles.
	}

	private _selectedConversation: Conversation | null = null;
	public get selectedConversation(): Conversation | null {
		return this._selectedConversation;
	}
	/**
	 * Le fil affiché est **la** donnée que l'adresse reflète (#995) : l'écrire ici, plutôt qu'à
	 * chacun des sept endroits qui changent de fil, garantit le miroir par construction.
	 *
	 * Un fil encore virtuel — le « Commentaires » d'un objet où personne n'a écrit, une
	 * discussion privée en cours de création — n'a pas d'identifiant serveur : le paramètre
	 * disparaît de l'adresse au lieu d'en désigner un faux.
	 */
	public set selectedConversation(conversation: Conversation | null) {
		this._selectedConversation = conversation;
		this.setConversationInUrl(conversation && !conversation.new ? conversation.id : null);
	}

	private _preiviousConversation: Conversation | null = null;

	/*----------------------------------------------------------------------------------------
	 * Accesseurs
	 ----------------------------------------------------------------------------------------*/



	/*----------------------------------------------------------------------------------------
	 * Méthodes publiques
	 ----------------------------------------------------------------------------------------*/

	public onCreationCancelled(): void {
		this.selectedConversation = this._preiviousConversation;
		this.showConversationForm = false;
	}

	public onClickNewConversation(): void {
		this._initConversation();
		this.showConversationForm = true;
	}

	public onConversationCreated(conversation: Conversation): void {
		this._registerConversation(conversation);
	}

	public onConversationChanged(conversation: Conversation): void {
		this.selectedConversation = conversation;
		// Mise à jour de la conversation dans la liste
		const index = this._conversations.findIndex(c => c.id === conversation.id);
		if (index !== -1) {
			this._conversations[index] = conversation;
		}
		this._conversations = this._conversations.slice(); // Trigger change detection
	}

	public onConversationSaved(conversation: Conversation): void {
		this.showConversationForm = false;
		this.selectedConversation = conversation;
		this._conversations.push(conversation);
		this._conversations = this._conversations.slice(); // Trigger change detection
	}

	public onConversationSelected(conversation: Conversation): void {
		this.showConversationForm = false;
		this._selectConversation(conversation);
	}

	/*----------------------------------------------------------------------------------------
	 * Méthodes protégées
	 ----------------------------------------------------------------------------------------*/

	protected override loadData(): void {
		// Fait ici (après binding des @Input) pour disposer de entityType/objectId.
		// On garantit une entrée publique « Commentaires » en tête de liste (virtuelle si elle
		// n'existe pas encore), puis on sélectionne le fil demandé, ou l'entrée publique.
		this._ensurePublicEntry();

		const conversation = this._initialConversation() ?? this._conversations.find(c => !c.private);
		if (conversation) {
			this._selectConversation(conversation);
		}
	}

	/*----------------------------------------------------------------------------------------
	 * Méthodes privées
	 ----------------------------------------------------------------------------------------*/

	private _buildVirtualPublicConversation(): Conversation {
		const conversation = Conversation.init();
		conversation.entityType = this.entityType;
		conversation.objectId = this.objectId;
		conversation.private = false;
		conversation.title = 'Commentaires';
		return conversation;
	}

	private _ensurePublicEntry(): void {
		// Le fil public « Commentaires » doit toujours être présent ET en tête de liste.
		const publicIndex = this._conversations.findIndex(c => !c.private);
		if (publicIndex === -1) {
			// Aucune conversation publique côté serveur : on prépend le fil virtuel « Commentaires ».
			this._conversations = [this._buildVirtualPublicConversation(), ...this._conversations];
		} else if (publicIndex > 0) {
			// Le fil public existe mais n'est pas premier : on le remonte en première position.
			const publicConversation = this._conversations[publicIndex];
			this._conversations = [
				publicConversation,
				...this._conversations.filter((_, index) => index !== publicIndex)
			];
		}
	}

	/**
	 * Fil demandé par l'adresse, s'il est bien porté par l'objet affiché.
	 *
	 * On le cherche parmi les fils de l'objet plutôt que de le lire au serveur : un fil qui
	 * n'y est pas ne le concerne pas — ou n'existe plus — et l'ouvrir reviendrait à afficher
	 * la discussion d'un autre objet dans cette fenêtre.
	 */
	private _initialConversation(): Conversation | undefined {
		if (!this.initialConversationId) {
			return undefined;
		}
		return this._conversations.find(c => c.id === this.initialConversationId);
	}

	private _initConversation(): void {
		this._preiviousConversation = this.selectedConversation;
		this.selectedConversation = Conversation.init();
		this.selectedConversation!.entityType = this.entityType;
		this.selectedConversation!.objectId = this.objectId;
		this.selectedConversation!.private = true;
	}

	private _loadConversation(conversation: Conversation): void {
		this.loading = true;
		this.conversationService.get$(conversation.id)
			.pipe(
				finalize(() => {
					this.loading = false;
				})
			)
			.subscribe((conversation: Conversation) => {
				this.selectedConversation = conversation;
				// Mise à jour de la conversation dans la liste
				const index = this._conversations.findIndex(c => c.id === conversation.id);
				if (index !== -1) {
					this._conversations[index] = conversation;
				}
				this._conversations = this._conversations.slice(); // Trigger change detection
			});
	}

	private _registerConversation(conversation: Conversation): void {
		// Le fil public virtuel vient d'être persisté (premier message). En général il est déjà
		// dans la liste (même référence, id/new mis à jour en place) : on rafraîchit juste la vue.
		if (!this._conversations.some(c => c.id === conversation.id)) {
			this._conversations.push(conversation);
		}
		this._conversations = this._conversations.slice(); // Trigger change detection
		this.selectedConversation = conversation;
	}

	private _selectConversation(conversation: Conversation): void {
		// Un fil virtuel (non persisté) n'a pas d'id serveur : on le sélectionne tel quel,
		// sinon on recharge la conversation persistée pour disposer de ses items.
		if (conversation.new) {
			this.selectedConversation = conversation;
		} else {
			this._loadConversation(conversation);
		}
	}

}
